// /admin/tenants/:id/bots/:botId/flows — the conversation flow editor.
// Loads a worker's raw Skill flows, shows the flow graph, and lets the operator
// edit each step's content + create new flows, saving live (validate → publish →
// cache invalidate via the skills-authoring endpoints). Per-step forms: Decir
// (msg), Preguntar (ask), Hacer (act), Fin (ok/handoff). Steps can be added
// (addStep) and deleted (deleteStep, healing the chain), and decision/route nodes
// branch on intents/conditions.

import { useQuery, useQueryClient } from '@tanstack/react-query'
// Un dato de la ficha dice QUÉ significa (`data_type` + `options`); un `ask` dice
// cómo INTERPRETA el Agente la respuesta (`extract`). La traducción entre esos
// dos vocabularios vive en shared y hay exactamente una: el generador de flujos
// de captura del servidor (C1) y estos pickers (C2) la comparten, porque dos
// copias de esa tabla es la deriva que este ciclo existe para terminar.
import {
  askExtractForDataType,
  FIELD_OPTIONS_SOURCES,
  type FieldOptionsSource,
} from '@vysto/shared/bot-config/field-kind-map'
import {
  buildStarterOrchestrator,
  DEFAULT_BRANCH,
} from '@vysto/shared/bot-config/orchestrator-scaffold'
// La coherencia de un Descubrir (opciones ↔ aristas ↔ rama por defecto al final)
// vive en shared: el generador de flujos de captura del servidor escribe rutas
// con ESTA misma regla, y dos reglas para una sola forma es como se desincronizan.
import {
  intentOfWhen,
  type RouteBranchRow,
  routeOptionsOf,
  routeRowsOf,
  writeRouteBranches,
} from '@vysto/shared/bot-config/route-branch'
import { triggerableIntents } from '@vysto/shared/intents'
import { isAxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { BusinessProfileModal } from './BusinessProfileModal.js'
import {
  buildWhen,
  COND_OPS,
  type Condition,
  flowVars,
  isDefaultWhen,
  parseWhen,
} from './flow-conditions.js'
import {
  type AgentSettings,
  type FichaFieldOption,
  type FlowGateway,
  flowSaveErrors,
  flowSelectLabel,
  type FunnelStageOption,
  getTools,
  isConfigOwnedFlow,
  operatorFlowGateway,
  ownerFlowGateway,
  type RawFlow,
  type RawNode,
  validateFlowClient,
  type WorkerFlow,
} from './flows-data.js'
import { KbPanel } from './KbPanel.js'
import { SimulatorPanel } from './SimulatorPanel.js'
import {
  Banner,
  Button,
  Card,
  type FlowEdgeData,
  FlowEditor,
  type FlowNodeData,
  FormField,
  Input,
  Modal,
  PageHeader,
  Select,
  type SkillValidation,
  Spinner,
  Textarea,
  toast,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { AgentSettingsForm } from '../workers/AgentSettingsForm.js'

const str = (v: unknown): string => (typeof v === 'string' ? v : '')

/** The API error code on a failed request (e.g. 'name_taken'), if any. */
function errorCode(e: unknown): string | undefined {
  return isAxiosError(e)
    ? (e.response?.data as { error?: { code?: string } } | undefined)?.error?.code
    : undefined
}

const NODE_LABEL: Record<RawNode['type'], string> = {
  msg: 'Mensaje',
  ask: 'Pregunta',
  act: 'Acción',
  dec: 'Decisión',
  answer: 'Responder',
  route: 'Descubrir',
  call: 'Llamar subflujo',
  handoff: 'Pasar con un asesor',
  fin: 'Fin',
}

// Plain-language explainer for each box type, shown in the editor legend so a
// non-builder can read the canvas. Keyed by the node types.
const NODE_HELP: { type: RawNode['type']; label: string; help: string }[] = [
  { type: 'msg', label: 'Mensaje', help: 'El agente dice algo.' },
  { type: 'ask', label: 'Pregunta', help: 'El agente pregunta y guarda la respuesta.' },
  {
    type: 'route',
    label: 'Descubrir',
    help: 'Detecta qué quiere el cliente y toma un camino según eso.',
  },
  {
    type: 'answer',
    label: 'Responder',
    help: 'Responde la pregunta del cliente con la base de conocimiento, y sigue.',
  },
  { type: 'act', label: 'Acción', help: 'Hace algo (agenda, cotiza, consulta…).' },
  { type: 'dec', label: 'Decisión', help: 'Toma un camino según la respuesta.' },
  {
    type: 'handoff',
    label: 'Pasar con un asesor',
    help: 'Transfiere a una persona y, cuando regresa al agente, sigue el flujo desde aquí.',
  },
  { type: 'fin', label: 'Fin', help: 'Termina: resuelto, o pasa a una persona.' },
]

/** A safe lowercase identifier from free text: strips accents, spaces → "_",
 *  drops other characters. Used for the trigger intent + capture var names. */
function slugify(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip combining accents (NFD-decomposed)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60)
}

function nodeDetail(n: RawNode): string | undefined {
  switch (n.type) {
    case 'msg':
      return n.config.text as string | undefined
    case 'ask':
      return n.config.prompt as string | undefined
    case 'act':
      return n.config.tool as string | undefined
    case 'call': {
      const label = str(n.config.label).trim()
      return label !== '' ? label : 'Sin subflujo asignado'
    }
    case 'handoff': {
      const reason = str(n.config.reason).trim()
      return reason !== '' ? reason : 'Pasa con una persona y regresa'
    }
    case 'fin':
      return (
        (n.config.reason as string | undefined) ??
        (n.config.kind === 'ok' ? 'Termina y regresa al agente' : 'Transfiere a una persona')
      )
    default:
      return undefined
  }
}

function stepLabel(n: RawNode): string {
  if (n.type === 'fin') return `Fin · ${n.config.kind === 'ok' ? 'OK' : 'Handoff'}`
  const d = nodeDetail(n)
  return d ? `${NODE_LABEL[n.type]}: ${d.slice(0, 24)}` : NODE_LABEL[n.type]
}

function defaultConfig(type: RawNode['type']): Record<string, unknown> {
  switch (type) {
    case 'msg':
      return { text: '' }
    case 'ask':
      return { var: '', prompt: '', extract: { kind: 'string' } }
    case 'act':
      return { tool: '', args: {}, out: '' }
    case 'route':
      return { var: 'intent' }
    case 'answer':
      return {}
    case 'call':
      return { skill_id: '' }
    case 'handoff':
      return { reason: '' }
    case 'fin':
      return { kind: 'ok' }
    default:
      return {}
  }
}

function toDisplay(flow: RawFlow): { nodes: FlowNodeData[]; edges: FlowEdgeData[] } {
  // The entry node — where every conversation begins (id 'start', else the first
  // node), matching the runtime's findStartNodeId.
  const startId = flow.nodes.find((n) => n.id === 'start')?.id ?? flow.nodes[0]?.id
  const nodes: FlowNodeData[] = flow.nodes.map((n, i) => {
    const detail = nodeDetail(n)
    return {
      id: n.id,
      type: n.type,
      position: n.position ?? { x: 40 + (i % 2) * 200, y: 20 + i * 120 },
      title:
        n.type === 'fin'
          ? `Fin · ${n.config.kind === 'ok' ? 'OK' : 'Handoff'}`
          : NODE_LABEL[n.type],
      ...(detail ? { detail } : {}),
      ...(n.type === 'fin' ? { finKind: n.config.kind as 'ok' | 'handoff' } : {}),
      ...(n.id === startId ? { isStart: true } : {}),
    }
  })
  const byId = new Map(flow.nodes.map((n) => [n.id, n]))
  const edges: FlowEdgeData[] = flow.edges.map((e) => {
    // Short, readable edge labels: a category condition shows the author's
    // plain-language label (e.g. "Quiere comprar"), the default branch shows
    // "si no", instead of the raw `vars.intent == "..."` expression.
    const intent = intentOfWhen(e.when)
    let intentLabel: string | undefined = intent
    if (intent !== undefined && intent !== DEFAULT_BRANCH) {
      const src = byId.get(e.from)
      if (src) intentLabel = routeOptionsOf(src).find((o) => o.key === intent)?.label ?? intent
    }
    const label =
      e.label ??
      (e.when ? (intent === DEFAULT_BRANCH ? 'si no' : (intentLabel ?? e.when)) : undefined)
    return { from: e.from, to: e.to, ...(label ? { label } : {}) }
  })
  return { nodes, edges }
}

const phaseOf = (n: RawNode): string => (typeof n.phase === 'string' ? n.phase.trim() : '')

/** Nodes in conversation order: BFS from start, then any unreachable nodes
 *  appended so nothing is dropped from the outline. */
function orderedNodes(flow: RawFlow): RawNode[] {
  const byId = new Map(flow.nodes.map((n) => [n.id, n]))
  const start = flow.nodes.find((n) => n.id === 'start') ?? flow.nodes[0]
  const seen = new Set<string>()
  const out: RawNode[] = []
  const queue: string[] = start ? [start.id] : []
  while (queue.length > 0) {
    const cur = queue.shift()!
    if (seen.has(cur)) continue
    seen.add(cur)
    const n = byId.get(cur)
    if (n) out.push(n)
    for (const e of flow.edges) if (e.from === cur) queue.push(e.to)
  }
  for (const n of flow.nodes) if (!seen.has(n.id)) out.push(n)
  return out
}

/** Group the flow's steps by phase, in first-appearance (conversation) order.
 *  Steps with no phase fall into a trailing empty-named group. */
function phaseOutline(flow: RawFlow): { phase: string; nodes: RawNode[] }[] {
  const groups: { phase: string; nodes: RawNode[] }[] = []
  for (const n of orderedNodes(flow)) {
    const p = phaseOf(n)
    let g = groups.find((x) => x.phase === p)
    if (!g) {
      g = { phase: p, nodes: [] }
      groups.push(g)
    }
    g.nodes.push(n)
  }
  return groups
}

/** Distinct phase names currently in use (for the reuse datalist). */
function phaseNames(flow: RawFlow): string[] {
  return [...new Set(flow.nodes.map(phaseOf).filter((p) => p !== ''))]
}

// The args an action passes to its tool, edited as JSON. Kept as local text so
// half-typed (temporarily invalid) JSON doesn't get discarded; the parent only
// receives a parsed object once it's valid. Values may contain ${var}
// placeholders — the save-time validator checks keys against the tool schema.
function ActArgsEditor({
  value,
  onChange,
}: {
  value: Record<string, unknown>
  onChange: (args: Record<string, unknown>) => void
}): JSX.Element {
  const [text, setText] = useState(() => JSON.stringify(value ?? {}, null, 2))
  const [error, setError] = useState('')

  function handle(next: string): void {
    setText(next)
    const trimmed = next.trim()
    if (trimmed === '') {
      setError('')
      onChange({})
      return
    }
    let parsed: unknown
    try {
      parsed = JSON.parse(trimmed)
    } catch {
      setError('JSON inválido.')
      return
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      setError('Debe ser un objeto, p. ej. { "fecha": "${fecha}" }.')
      return
    }
    setError('')
    onChange(parsed as Record<string, unknown>)
  }

  return (
    <FormField label="Datos que recibe la acción (JSON)" htmlFor="act-args">
      <Textarea
        id="act-args"
        value={text}
        onChange={(e) => handle(e.target.value)}
        className="min-h-[96px] font-mono text-[12px]"
        placeholder={'{\n  "fecha": "${fecha}"\n}'}
      />
      {error ? <p className="mt-1 text-[11px] text-sens">{error}</p> : null}
    </FormField>
  )
}

/** The choices a "Pregunta · Opción de una lista" offers (extract.kind = enum).
 *  The runtime matches the customer's reply to one of these values. */
function EnumValuesEditor({
  values,
  onChange,
}: {
  values: string[]
  onChange: (v: string[]) => void
}): JSX.Element {
  return (
    <FormField label="Opciones que el cliente puede elegir" htmlFor="enum-values">
      <div className="space-y-2">
        {values.map((v, i) => (
          <div key={`opt-${String(i)}`} className="flex items-center gap-2">
            <Input
              value={v}
              onChange={(e) => onChange(values.map((x, j) => (j === i ? e.target.value : x)))}
              placeholder={`Opción ${String(i + 1)}`}
              aria-label={`Opción ${String(i + 1)}`}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onChange(values.filter((_, j) => j !== i))}
            >
              Quitar
            </Button>
          </div>
        ))}
        <Button variant="secondary" size="sm" onClick={() => onChange([...values, ''])}>
          Agregar opción
        </Button>
      </div>
    </FormField>
  )
}

// ─── Los dos pickers de datos de la ficha (C2) ───────────────────────────────
// Antes, tanto la clave de "Recordar" como el nombre de una Pregunta se escribían
// a mano. Escribir de memoria la clave de un dato es cómo un flujo termina
// guardando `presupesto` en un espacio cuya ficha dice `presupuesto`: nadie se
// entera hasta que la tarjeta llega vacía. Los dos pickers ofrecen la ficha real
// del espacio; el texto libre SIGUE permitido (hay capturas legítimas que no son
// un dato de ninguna ficha) pero queda marcado, para que se vea que se eligió.

/** El valor centinela de la entrada "campo libre" de ambos pickers. No es una
 *  clave que alguien pueda autorar: es el marcador de "esta fila no es un dato de
 *  la ficha", que es justo lo que hay que poder ver. */
const FREE_FIELD = '__campo_libre__'

/** El valor centinela de la opción "Otra (escribirla)" del selector de
 *  intención al crear un flujo. Solo vive en el estado del modal: nunca se
 *  envía, porque al enviar se sustituye por lo que el autor escribió. */
const CUSTOM_INTENT = '__otra__'

/** Lo que se lee debajo de una fila en texto libre, en los dos pickers. Un
 *  campo libre no es un error, pero tampoco puede parecerse a un dato de la
 *  ficha: sin esta línea, las dos cosas se ven idénticas. */
const FREE_FIELD_NOTE = 'Campo libre: se guarda tal cual, no es un dato de la ficha.'

/** La fuente del dato, si el servidor mandó una del conjunto cerrado (D21-4).
 *
 *  Se COMPRUEBA en vez de castear: el payload de la ficha llega como texto y la
 *  unión la define la base. Un valor que no reconocemos — un servidor más nuevo
 *  con una cuarta fuente — se lee como "sin fuente", que deja la pregunta
 *  exactamente como estaba antes de este PR en vez de meter en el flujo un
 *  `source` que el runtime no sabe resolver. */
function fieldOptionsSourceOf(value: string | undefined): FieldOptionsSource | undefined {
  if (value === undefined || value === 'lista') return undefined
  return (FIELD_OPTIONS_SOURCES as readonly string[]).includes(value)
    ? (value as FieldOptionsSource)
    : undefined
}

/** `interes` NO es un dato de la ficha: el Área de interés es una COLUMNA de la
 *  tarjeta, y el servidor rechaza con 400 `flow_invalid` un `capturar_lead` cuyos
 *  `campos` la traigan. Por eso se filtra de las opciones — ofrecer una clave que
 *  el guardado rechaza es una puerta pintada — y por eso una fila ya autorada con
 *  ella se conserva y se señala en vez de desaparecer sola. */
const INTERES_KEY = 'interes'

/** Escribe un argumento OPCIONAL de texto: si queda vacío, la clave se BORRA de
 *  `args` en vez de guardarse como ''. La diferencia importa río abajo — el tool
 *  declara estos argumentos como `z.string().min(1).optional()`, así que la clave
 *  ausente es "no lo mandes" y la clave con '' es un dato inválido: el flujo se
 *  publica igual (la regla 6 solo mira claves) y luego revienta en vivo con
 *  "String must contain at least 1 character(s)". Vaciar un campo es quitarlo. */
function setOptionalArg(
  args: Record<string, unknown>,
  key: string,
  value: string,
): Record<string, unknown> {
  if (value.trim() === '') {
    const { [key]: _dropped, ...rest } = args
    return rest
  }
  return { ...args, [key]: value }
}

/** Friendly fields for the CRM `capturar_lead` action (orchestrator M5a) instead
 *  of raw JSON: name / stage / interest + a "Recordar" key→value list that the
 *  flow persists on the lead so a returning contact isn't re-asked. Writes the
 *  tool's `args` (incl. `campos`). */
function CrmLeadArgsEditor({
  args,
  stages,
  ficha,
  onChange,
}: {
  args: Record<string, unknown>
  /** The tenant's OWN funnel stages. The picker used to hard-code five keys;
   *  stages are tenant data now, so an authored `etapa` that isn't in this list
   *  is a stage the tool will reject at run time. */
  stages: FunnelStageOption[]
  /** La ficha de cada proceso, por clave de proceso. Es lo que "Recordar"
   *  ofrece: los datos que ESE proceso realmente lleva. */
  ficha: Record<string, FichaFieldOption[]>
  onChange: (a: Record<string, unknown>) => void
}): JSX.Element {
  const s = (v: unknown): string => (typeof v === 'string' ? v : '')
  // Which funnel this node captures into. A tenant may own several since PR 17,
  // and a stage key only means something inside one of them — so the funnel is
  // picked FIRST and the stage options are the chosen funnel's, not a flat list
  // in which two "Nuevo" entries are indistinguishable.
  const pipelines: { key: string; name: string; isDefault: boolean }[] = []
  for (const st of stages) {
    if (!pipelines.some((p) => p.key === st.pipelineKey)) {
      pipelines.push({
        key: st.pipelineKey,
        name: st.pipelineName,
        isDefault: st.isDefaultPipeline,
      })
    }
  }
  // Where an unset node lands: the tenant's default funnel, exactly as the tool
  // resolves it server-side when `embudo` is absent.
  const defaultPipeline = pipelines.find((p) => p.isDefault)?.key ?? pipelines[0]?.key ?? ''
  const pipelineKey = s(args.embudo) || defaultPipeline
  const funnelStages = stages.filter((st) => st.pipelineKey === pipelineKey)
  // Where an unset node lands: that funnel's first open stage (F20 — no
  // capture marker). The tool applies the same rule server-side when `etapa`
  // is absent, so the picker shows the truth rather than a guess.
  const defaultStage = funnelStages.find((st) => st.kind === 'open')?.key ?? ''
  // Lo que la etapa elegida dice que OBTIENE (CP3 L). Es una pista para quien
  // escribe el flujo, no una regla: "Recordar" sigue aceptando cualquier clave y
  // el tool valida exactamente lo mismo que antes. Sin esto, la caja de
  // Recordar era texto libre sin ninguna señal de qué espera la etapa destino.
  const targetStage = funnelStages.find((st) => st.key === (s(args.etapa) || defaultStage))
  const gatheredHint = targetStage?.gatheredLabels ?? []
  // Las claves crudas de lo mismo: la pista se LEE con etiquetas, el picker
  // ESCRIBE claves, y por eso el servidor manda las dos listas en el mismo orden.
  const gatheredKeys = targetStage?.gatheredKeys ?? []
  // Los datos que este proceso realmente lleva, menos `interes` (ver INTERES_KEY).
  const fichaFields = (ficha[pipelineKey] ?? []).filter((f) => f.key !== INTERES_KEY)
  // CÓMO se ve la sugerencia. El Select del design system es un <select> nativo
  // plano, sin optgroup y a propósito (listas cortas, teclado y lector de
  // pantalla gratis). Así que la sugerencia se expresa con las dos señales que un
  // <select> plano sí tiene: ORDEN (lo que la etapa obtiene va primero) y
  // ETIQUETA (un sufijo que lo dice con palabras). Se eligió esto sobre agregar
  // optgroup al componente compartido: cambiar un componente que usa toda la
  // aplicación por una sola pantalla es el precio equivocado, y el sufijo además
  // sobrevive a un lector de pantalla, que es donde un agrupamiento visual no.
  const suggestedFields = fichaFields.filter((f) => gatheredKeys.includes(f.key))
  const otherFields = fichaFields.filter((f) => !gatheredKeys.includes(f.key))
  const fieldOptions = [
    ...suggestedFields.map((f) => ({
      value: f.key,
      label: `${f.label} · lo que esta etapa obtiene`,
    })),
    ...otherFields.map((f) => ({ value: f.key, label: f.label })),
    { value: FREE_FIELD, label: 'Otro dato (campo libre)' },
  ]
  const campos: Record<string, unknown> =
    args.campos && typeof args.campos === 'object' && !Array.isArray(args.campos)
      ? (args.campos as Record<string, unknown>)
      : {}
  // Rows live in local state so a freshly-added (empty-key) row stays editable —
  // serialising directly to the object would drop it before you could type.
  const [rows, setRows] = useState<[string, string][]>(() =>
    Object.entries(campos).map(([k, v]) => [k, s(v)]),
  )
  const setCampos = (next: [string, string][]): void => {
    setRows(next)
    const obj: Record<string, unknown> = {}
    for (const [k, val] of next) if (k.trim() !== '') obj[k] = val
    onChange({ ...args, campos: obj })
  }
  const entries = rows
  return (
    <div className="space-y-4">
      <FormField label="Nombre del prospecto" htmlFor="crm-nombre">
        <Input
          value={s(args.nombre)}
          onChange={(e) => onChange({ ...args, nombre: e.target.value })}
          placeholder="${nombre}"
        />
      </FormField>
      {/* The picker's LABEL is the only thing that reads "Proceso": `embudo` is
          the capture-lead tool's INPUT FIELD name and every saved flow carries
          that key, so the identifier stays put. */}
      {pipelines.length > 1 && (
        <FormField
          label="Proceso"
          htmlFor="crm-embudo"
          helper="Solo aplica a prospectos nuevos: si el contacto ya tiene tarjeta, se queda en su proceso."
        >
          <Select
            value={pipelineKey}
            onChange={(e) => {
              // Changing the funnel invalidates the stage: a key from the old
              // one would silently resolve to that funnel's capture stage at run
              // time. Clearing it re-selects the new funnel's default in the
              // Select below, which is the honest starting point.
              const { etapa: _dropped, ...rest } = args
              onChange(setOptionalArg(rest, 'embudo', e.target.value))
            }}
            options={pipelines.map((p) => ({ value: p.key, label: p.name }))}
          />
        </FormField>
      )}
      <FormField label="Etapa en el proceso" htmlFor="crm-etapa">
        <Select
          value={s(args.etapa) || defaultStage}
          onChange={(e) => onChange(setOptionalArg(args, 'etapa', e.target.value))}
          options={funnelStages.map((st) => ({ value: st.key, label: st.name }))}
        />
      </FormField>
      {gatheredHint.length > 0 && (
        <p className="text-[11.5px] leading-relaxed text-text-3" data-testid="crm-stage-gathered">
          Esta etapa espera: {gatheredHint.join(', ')}.
        </p>
      )}
      <FormField label="Interés (etiqueta de la tarjeta)" htmlFor="crm-interes">
        <Input
          value={s(args.interes)}
          onChange={(e) => onChange(setOptionalArg(args, 'interes', e.target.value))}
          placeholder="p. ej. ${modelo}"
        />
      </FormField>
      <FormField label="Recordar (para no volver a preguntar)" htmlFor="crm-campos">
        <div className="space-y-2">
          {entries.map(([k, val], i) => {
            // Una fila es "de la ficha" cuando su clave está en la lista que el
            // proceso declara. Todo lo demás (incluida la fila recién agregada,
            // todavía sin clave) es texto libre, y se ve como tal.
            const known = fichaFields.some((f) => f.key === k)
            const isInteres = k.trim() === INTERES_KEY
            return (
              <div key={`campo-${String(i)}`} className="flex flex-wrap items-center gap-2">
                <Select
                  value={known ? k : FREE_FIELD}
                  onChange={(e) =>
                    setCampos(
                      entries.map((en, j) =>
                        j === i
                          ? // Volver a "campo libre" vacía la clave: la fila sigue
                            // ahí y editable (misma regla de siempre — una clave en
                            // blanco no se serializa hasta que se escribe).
                            [e.target.value === FREE_FIELD ? '' : e.target.value, en[1]]
                          : en,
                      ),
                    )
                  }
                  options={fieldOptions}
                  aria-label={`Dato ${String(i + 1)}`}
                  className="max-w-[220px]"
                />
                {!known && (
                  <Input
                    value={k}
                    onChange={(e) =>
                      setCampos(entries.map((en, j) => (j === i ? [e.target.value, en[1]] : en)))
                    }
                    placeholder="dato"
                    aria-label={`Clave del dato ${String(i + 1)}`}
                    className="max-w-[120px]"
                  />
                )}
                <span aria-hidden className="text-[12px] text-text-3">
                  =
                </span>
                <Input
                  value={s(val)}
                  onChange={(e) =>
                    setCampos(entries.map((en, j) => (j === i ? [en[0], e.target.value] : en)))
                  }
                  placeholder="${presupuesto}"
                  aria-label={`Valor ${String(i + 1)}`}
                  className="max-w-[140px]"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCampos(entries.filter((_, j) => j !== i))}
                >
                  Quitar
                </Button>
                {!known && <p className="basis-full text-[11px] text-text-3">{FREE_FIELD_NOTE}</p>}
                {isInteres && (
                  <p className="basis-full text-[11px] text-sens">
                    La clave «interes» no puede ir en Recordar: el Área de interés es una columna de
                    la tarjeta, no un dato de la ficha. Escribe esa respuesta en el campo Interés de
                    este mismo paso.
                  </p>
                )}
              </div>
            )
          })}
          <Button variant="secondary" size="sm" onClick={() => setCampos([...entries, ['', '']])}>
            Agregar dato
          </Button>
        </div>
      </FormField>
    </div>
  )
}

/** The agent's general (non-flow) settings — identity, the no-match message, and
 *  the handoff "safety net". Lives in the ONE editor (orchestrator M6) so there's
 *  no separate "Ajustar" page: the conversation is the flow, and this is the
 *  small Settings beside it. Loads + saves through the gateway's owner-only
 *  `settings` capability. */
// The IANA timezones the agent can schedule citas in — Mexico's zones, labelled
// plainly (a raw "America/Tijuana" means nothing to a clinic owner).

function AgentSettingsModal({
  settings,
  onClose,
}: {
  settings: NonNullable<FlowGateway['settings']>
  onClose: () => void
}): JSX.Element {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['flow-editor', 'agent-settings'],
    queryFn: () => settings.load(),
  })
  const [form, setForm] = useState<AgentSettings | null>(null)
  const [busy, setBusy] = useState(false)
  const value = form ?? data ?? null

  async function save(): Promise<void> {
    if (!value) return
    setBusy(true)
    try {
      // Identity lives in "Perfil del negocio" now; Ajustes saves the rest and
      // leaves the loaded identityProfile untouched.
      await settings.save(value)
      await qc.invalidateQueries({ queryKey: ['flow-editor', 'agent-settings'] })
      toast.ok('Ajustes guardados.')
      onClose()
    } catch {
      toast.error('No se pudieron guardar los ajustes.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Ajustes del agente"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={() => void save()} loading={busy} disabled={!value}>
            Guardar ajustes
          </Button>
        </>
      }
    >
      {isLoading || !value ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        <AgentSettingsForm value={value} onChange={setForm} />
      )}
    </Modal>
  )
}

export function FlowEditorScreen({ gateway }: { gateway: FlowGateway }): JSX.Element {
  const navigate = useNavigate()
  const {
    data: flows,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['flows', gateway.scopeKey],
    queryFn: gateway.list,
  })

  const [skillIdx, setSkillIdx] = useState(0)
  const [draft, setDraft] = useState<RawFlow | null>(null)
  const [selectedId, setSelectedId] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [errors, setErrors] = useState<{ nodeId?: string; rule: string; message: string }[]>([])
  // Somebody published a newer version of this flow while it was open (another
  // person, or the capture-flow generator on Procesos). We did NOT overwrite
  // them and we do not throw the draft away: the author reloads and re-applies.
  const [conflict, setConflict] = useState(false)
  // Which flow the draft was loaded from, so a post-save refetch (same flow,
  // new version) is told apart from actually switching flows.
  const loadedSkillId = useRef('')

  // F2-3: la tarjeta del agente enlaza a UN flujo (`?flow=<id>`), no a la lista.
  // Se aplica UNA sola vez, al llegar: si se reaplicara, cambiar de flujo con el
  // selector devolvería al autor al del enlace en cada recarga de la consulta.
  const [searchParams] = useSearchParams()
  const requestedFlowId = searchParams.get('flow')
  const appliedFlowParam = useRef(false)
  useEffect(() => {
    if (appliedFlowParam.current || !requestedFlowId || !flows) return
    appliedFlowParam.current = true
    const idx = flows.findIndex((f) => f.id === requestedFlowId)
    if (idx >= 0) setSkillIdx(idx)
  }, [flows, requestedFlowId])

  // F4-1 (D2): sin `?flow=`, una URL pelada abría flows[0], y la lista llega
  // ordenada por nombre (`ORDER BY s.name ASC`), así que "Captura X" le ganaba a
  // "Saludo" y el editor abría un flujo de captura cualquiera. El flujo
  // principal es el que dirige la conversación: ese abre. También una sola vez,
  // para que cambiar de flujo con el selector no rebote, y el parámetro manda.
  const appliedDefaultFlow = useRef(false)
  useEffect(() => {
    if (appliedDefaultFlow.current || requestedFlowId || !flows || flows.length === 0) return
    appliedDefaultFlow.current = true
    const idx = flows.findIndex((f) => f.role === 'orchestrator')
    if (idx > 0) setSkillIdx(idx)
  }, [flows, requestedFlowId])

  // The bot's vertical drives the starter orchestrator we regenerate on the
  // one-click "Crear flujo principal".
  const { data: catalog } = useQuery({
    queryKey: ['flow-templates', gateway.scopeKey],
    queryFn: gateway.templates,
  })

  // The Asesores a `handoff` node can route to (owner gateway only). Loaded once
  // so the handoff property panel can offer an "assign to" picker.
  const { data: assignableMembers } = useQuery({
    queryKey: ['flow-assignable-members', gateway.scopeKey],
    queryFn: () => gateway.assignableMembers!(),
    enabled: gateway.assignableMembers !== undefined,
  })

  // The registered act-node tools (incl. capturar_lead / Guardar en CRM), so the
  // act panel can suggest them instead of the author typing names from memory.
  const { data: toolCatalog } = useQuery({ queryKey: ['flow-tools'], queryFn: getTools })

  // The tenant's funnel stages, for the capturar_lead node's stage picker. The
  // funnel is tenant data — the editor offers what this client actually has.
  const { data: pipelineStagesData } = useQuery({
    queryKey: ['flow-pipeline-stages', gateway.scopeKey],
    queryFn: gateway.pipelineStages,
  })
  const pipelineStages: FunnelStageOption[] = pipelineStagesData?.stages ?? []
  const pipelineFicha: Record<string, FichaFieldOption[]> = pipelineStagesData?.ficha ?? {}
  // Lo que una Pregunta puede ofrecer: la UNIÓN de las fichas de todos los
  // procesos, sin repetir clave. Un `ask` no está pegado a ningún proceso (puede
  // correr antes de que se decida a dónde aterriza la captura), así que no hay
  // forma de saber cuál ficha alimenta; ofrecer solo la del proceso por omisión
  // escondería datos que el flujo sí va a necesitar. Se conserva la primera
  // aparición: cuando dos procesos comparten clave, comparten el dato.
  const askFichaFields: FichaFieldOption[] = []
  for (const list of Object.values(pipelineFicha)) {
    for (const f of list) if (!askFichaFields.some((x) => x.key === f.key)) askFichaFields.push(f)
  }

  const skill: WorkerFlow | undefined = flows?.[skillIdx]
  const orchestrator = flows?.find((f) => f.role === 'orchestrator')
  const [orchBusy, setOrchBusy] = useState(false)
  const [orchError, setOrchError] = useState('')
  const [confirmDel, setConfirmDel] = useState(false)
  const [delBusy, setDelBusy] = useState(false)
  const [delError, setDelError] = useState('')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [kbOpen, setKbOpen] = useState(false)
  // W4'-6c (U2): la puerta "Probar el agente" de Configuración → Procesos llega
  // con `?probar=1` y encuentra el simulador ya abierto, en vez de aterrizar en
  // el editor a buscar el botón. Sólo el estado INICIAL: cerrarlo no lo reabre.
  // El render ya exige `gateway.simulator`, así que sin capacidad el parámetro
  // no dibuja nada.
  const [simOpen, setSimOpen] = useState(() => searchParams.get('probar') === '1')
  // B5: the seeded template library, finally reachable — the new-flow modal
  // offers it and sends templateId; "Guardar como plantilla" feeds it back.
  const [newOpen, setNewOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newTemplate, setNewTemplate] = useState('')
  // What the flow is FOR. Until P8 the modal never sent an intent, so its own
  // default option ('En blanco') always 400'd `intent_required` and the blank
  // path was a painted door. CUSTOM_INTENT reveals a free-slug field, because
  // the API accepts any 60-char string and the vertical list is a starting
  // vocabulary, not a cage.
  const [newIntent, setNewIntent] = useState('')
  const [newIntentCustom, setNewIntentCustom] = useState('')
  const [newBusy, setNewBusy] = useState(false)
  const [newError, setNewError] = useState('')
  const [tplOpen, setTplOpen] = useState(false)
  const [tplName, setTplName] = useState('')
  const [tplDesc, setTplDesc] = useState('')
  const [tplBusy, setTplBusy] = useState(false)
  const [tplError, setTplError] = useState('')

  async function createTaskFlow(): Promise<void> {
    if (!newName.trim()) {
      setNewError('Ponle nombre al flujo.')
      return
    }
    const intent = (newIntent === CUSTOM_INTENT ? newIntentCustom : newIntent).trim()
    // A flow with no intent can never be dispatched, so the server refuses it.
    // Say so HERE, in the modal, instead of letting the round-trip explain it:
    // the only exception is a template that carries its own default trigger,
    // which the server lifts when we send nothing.
    if (!intent && !newTemplate) {
      setNewError('Indica para qué sirve el flujo.')
      return
    }
    setNewBusy(true)
    setNewError('')
    try {
      const created = await gateway.create({
        name: newName.trim(),
        ...(intent ? { intent } : {}),
        ...(newTemplate ? { templateId: newTemplate } : {}),
      })
      setNewOpen(false)
      setNewName('')
      setNewTemplate('')
      setNewIntent('')
      setNewIntentCustom('')
      const res = await refetch()
      const idx = res.data?.findIndex((f) => f.id === created.id) ?? -1
      if (idx >= 0) setSkillIdx(idx)
    } catch (err) {
      setNewError(apiErrorMessage(err, 'No se pudo crear el flujo.'))
    } finally {
      setNewBusy(false)
    }
  }

  async function saveCurrentAsTemplate(): Promise<void> {
    if (!gateway.saveAsTemplate || !skill) return
    if (!tplName.trim()) {
      setTplError('Ponle nombre a la plantilla.')
      return
    }
    setTplBusy(true)
    setTplError('')
    try {
      await gateway.saveAsTemplate(skill.id, {
        name: tplName.trim(),
        description: tplDesc.trim(),
      })
      setTplOpen(false)
      setTplName('')
      setTplDesc('')
      toast.ok('Plantilla guardada', {
        description: 'Disponible al crear flujos para clientes de esta vertical.',
      })
    } catch (err) {
      setTplError(apiErrorMessage(err, 'No se pudo guardar la plantilla.'))
    } finally {
      setTplBusy(false)
    }
  }

  // Delete the whole current flow (all versions). Used to clean up old/leftover
  // flows. After deleting, fall back to the first remaining flow.
  async function deleteCurrentFlow(): Promise<void> {
    if (!gateway.deleteFlow || !skill) return
    setDelBusy(true)
    setDelError('')
    try {
      await gateway.deleteFlow(skill.id)
      setConfirmDel(false)
      setSkillIdx(0)
      await refetch()
    } catch (err) {
      setDelError(apiErrorMessage(err, 'No se pudo eliminar el flujo.'))
    } finally {
      setDelBusy(false)
    }
  }

  // One-click: create + publish a ready-to-run orchestrator (main flow) via the
  // real API (which invalidates the bot's config cache), then select it.
  async function createOrchestrator(): Promise<void> {
    setOrchBusy(true)
    setOrchError('')
    try {
      // Find a free name: a leftover flow may already use "Flujo principal"
      // (e.g. from an earlier failed attempt), which would 409 on the name.
      // Skip past it rather than dead-end. `orchestrator_exists` is a different
      // 409 — surfaced, not retried (there's already a main flow).
      let created: { id: string } | undefined
      for (let attempt = 0; attempt < 6 && !created; attempt++) {
        const name = attempt === 0 ? 'Flujo principal' : `Flujo principal ${attempt + 1}`
        try {
          created = await gateway.create({ name, role: 'orchestrator' })
        } catch (e) {
          if (errorCode(e) === 'name_taken') continue
          throw e
        }
      }
      if (!created) throw new Error('No se encontró un nombre libre para el flujo principal.')
      await gateway.save(
        created.id,
        buildStarterOrchestrator(
          catalog?.vertical,
          '¡Hola! Soy el asistente. ¿En qué puedo ayudarle?',
        ),
        // The flow was created one line ago: version 1 is the only version.
        1,
      )
      const res = await refetch()
      const idx = res.data?.findIndex((f) => f.id === created.id) ?? -1
      if (idx >= 0) setSkillIdx(idx)
    } catch (err) {
      setOrchError(apiErrorMessage(err, 'No se pudo crear el flujo principal.'))
    } finally {
      setOrchBusy(false)
    }
  }

  // Status banner: tells the owner what actually drives the conversation, so a
  // bot with no orchestrator (the agent answers with plain AI by default) is
  // never a silent mystery. Owner surfaces only (operators can't create one).
  const orchestratorBanner = gateway.supportsOrchestrator ? (
    orchestrator ? (
      <Banner tone="ok">
        <b>Flujo principal activo: {orchestrator.name}.</b> Este flujo dirige toda la conversación
        desde el primer mensaje del cliente.
      </Banner>
    ) : (
      <Banner tone="warn">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span>
            <b>Sin flujo principal.</b> El agente responde con IA por defecto; tus otros flujos solo
            se activan cuando el cliente pide algo específico. Crea uno para que dirija toda la
            conversación.
          </span>
          <Button size="sm" onClick={() => void createOrchestrator()} loading={orchBusy}>
            Crear flujo principal
          </Button>
        </div>
        {orchError ? <p className="mt-1.5 text-[12px] text-sens">{orchError}</p> : null}
      </Banner>
    )
  ) : null

  // EL AVISO DE DERIVA, DONDE SE EDITA (F4-3). El flujo abierto dejó de
  // coincidir con la ficha del proceso del que sale, y hasta ahora eso sólo se
  // veía en Configuración → Procesos: quien abría el editor para cambiar una
  // pregunta no tenía forma de saber que el flujo entero ya iba atrasado. Se
  // dice aquí, con el camino que sí lo arregla — se edita el proceso y el flujo
  // se actualiza al publicar el agente, no a mano paso por paso.
  const driftBanner =
    skill?.drift?.state === 'drift' ? (
      <div data-testid="flow-drift-banner">
        <Banner tone="warn">
          No coincide con tu configuración. Se actualiza al publicar el agente desde{' '}
          <Link to="/configuracion?tab=embudos" className="font-semibold text-link underline">
            Procesos
          </Link>
          .
        </Banner>
      </div>
    ) : null

  // UN FLUJO COMPILADO SE LEE, NO SE EDITA (F4-9). El flujo abierto sale de la
  // configuración del cliente, así que el editor lo presenta en solo lectura:
  // guardar a mano escribiría encima de lo que el compilador vuelve a escribir
  // en la siguiente generación, y el cambio se perdería sin avisar. La pregunta
  // se cambia donde SE ESCRIBE, que es el proceso.
  //
  // La derivación es la MISMA de la etiqueta de autoría (`isConfigOwnedFlow`),
  // y desde W4'-4b esa regla lee `skills.managed`: la PROCEDENCIA de verdad, no
  // el veredicto de deriva con el que se sostuvo mientras nadie prendía la
  // columna. Cambia dos cosas aquí: un flujo comparado contra la ficha que nadie
  // publicó vuelve a ser editable (era lo único que tenía), y el flujo principal
  // ADMINISTRADO deja de ser una excepción por rol, porque el Publicar lo compila
  // igual que a los demás y editarlo a mano se perdería en la siguiente vuelta.
  //
  // Ojo con lo que NO hace: no toca `managed`, no manda `asCompiler` y no hace
  // que el repositorio rechace la escritura. Los ajustes de un `ask` que el
  // editor todavía es el único en escribir (retry_prompt, max_retries,
  // no_prefill, on_unexpected) siguen viviendo en el flujo y el merge los
  // conserva; esto es la mitad VISIBLE, no el candado.
  const configOwned = skill ? isConfigOwnedFlow(skill) : false

  // De dónde viene lo que se está leyendo, y a dónde ir a cambiarlo. Tono
  // neutral a propósito: no pasa nada malo, así que no es una advertencia. Y
  // convive con el de deriva (F4-3), que sí lo es: cuando aplican los dos, la
  // deriva va primero porque pide una acción y esto solo explica.
  const configOwnedBanner = configOwned ? (
    <div data-testid="flow-config-owned-banner">
      <Banner tone="info">
        Este flujo lo escribe tu configuración de Procesos. Para cambiar lo que pregunta, edita el
        proceso.{' '}
        <Link to="/configuracion?tab=embudos" className="font-semibold text-link underline">
          Ir a Procesos
        </Link>
        .
      </Banner>
    </div>
  ) : null

  // Reset the working copy whenever the loaded skill changes.
  useEffect(() => {
    if (skill) {
      setDraft(structuredClone(skill.flow))
      setSelectedId(skill.flow.nodes[0]?.id ?? '')
      setErrors([])
      setConflict(false)
      // A successful save refetches, and the SAME flow comes back one version
      // newer — that is not "another flow was loaded", so it must not wipe the
      // "Guardado y publicado." confirmation the author just earned. Only a
      // real flow switch clears it.
      if (loadedSkillId.current !== skill.id) {
        loadedSkillId.current = skill.id
        setSaved(false)
      }
    }
  }, [skill])

  const backButton = (
    <Button variant="secondary" onClick={() => void navigate(gateway.backHref)}>
      ← Volver
    </Button>
  )

  if (isLoading) return <Spinner label="Cargando flujo…" />

  if (!flows || flows.length === 0 || !skill || !draft) {
    return (
      <div className="mx-auto w-full max-w-5xl">
        <PageHeader
          title="Conversación"
          subtitle="Lo que hace el agente paso a paso para un objetivo."
          actions={<div className="flex items-center gap-2">{backButton}</div>}
        />
        {gateway.supportsOrchestrator && !orchestrator ? null : orchestratorBanner ? (
          <div className="mt-4">{orchestratorBanner}</div>
        ) : null}
        <Card className="mt-4">
          <Card.Body>
            {gateway.supportsOrchestrator ? (
              // Owner: one clear path — the vertical-shaped orchestrator, ready to
              // run. No menu of options (we always want an orchestrator to start).
              <div className="mx-auto max-w-[34rem] py-6 text-center">
                <h2 className="text-[15px] font-bold text-text">
                  Tu agente todavía no tiene su flujo de conversación
                </h2>
                <p className="mt-2 text-[13px] leading-relaxed text-text-2">
                  El <strong>flujo principal</strong> dirige toda la conversación: saluda, entiende
                  qué necesita el cliente (agendar una cita, cotizar, una pregunta), lo resuelve y,
                  si hace falta, lo pasa con una persona. Lo armamos listo para tu tipo de negocio;
                  luego lo ajustas a tu gusto.
                </p>
                <div className="mt-4 flex justify-center">
                  <Button onClick={() => void createOrchestrator()} loading={orchBusy}>
                    Crear el flujo principal
                  </Button>
                </div>
                {orchError ? <p className="mt-2 text-[12px] text-sens">{orchError}</p> : null}
              </div>
            ) : (
              <div className="mx-auto max-w-[34rem] py-6 text-center">
                <h2 className="text-[15px] font-bold text-text">
                  Este agente todavía no tiene su flujo de conversación
                </h2>
                <p className="mt-2 text-[13px] leading-relaxed text-text-2">
                  El flujo principal se crea automáticamente al dar de alta el agente. Si no
                  aparece, vuelve a cargar la página; si sigue faltando, avísale a soporte.
                </p>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    )
  }

  /** Every edit to the draft goes through here. Besides dropping the "guardado"
   *  confirmation it CLEARS the blocking errors the save endpoint returned: they
   *  describe the flow as it was when it was sent, and the author just changed
   *  it. Keeping them made the save button unclickable forever — deleting the
   *  very step an error pointed at left the error (and the disabled button)
   *  behind, with nothing marked on the canvas to explain it, until a reload
   *  (F40). Structural problems still block live via `validateFlowClient`, and
   *  the server re-runs the full validation on the next save. */
  function markEdited(): void {
    setSaved(false)
    setErrors([])
  }

  function updateNodeConfig(nodeId: string, patch: Record<string, unknown>): void {
    markEdited()
    setDraft((f) =>
      f
        ? {
            ...f,
            nodes: f.nodes.map((n) =>
              n.id === nodeId ? { ...n, config: { ...n.config, ...patch } } : n,
            ),
          }
        : f,
    )
  }

  // Assign (or clear) a step's phase grouping label. Stored as node metadata;
  // the runtime ignores it.
  function setNodePhase(nodeId: string, phase: string): void {
    markEdited()
    setDraft((f) =>
      f
        ? {
            ...f,
            nodes: f.nodes.map((n) => {
              if (n.id !== nodeId) return n
              const trimmed = phase.trim()
              if (trimmed === '') {
                const { phase: _drop, ...rest } = n
                return rest
              }
              return { ...n, phase: trimmed }
            }),
          }
        : f,
    )
  }

  // Add a step on the path to fin.ok (X→finOk becomes X→new→finOk), so it's
  // never an orphan or dead-end.
  function addStep(type: RawNode['type']): void {
    const nid = `s_${Math.random().toString(36).slice(2, 8)}`
    markEdited()
    setDraft((f) => {
      if (!f) return f
      const node: RawNode = { id: nid, type, config: defaultConfig(type) }
      const finOk = f.nodes.find((n) => n.type === 'fin' && n.config.kind === 'ok')
      let edges = [...f.edges]
      if (finOk) {
        const intoOk = edges.find((e) => e.to === finOk.id)
        if (intoOk) {
          edges = edges.filter((e) => e !== intoOk)
          edges.push({ from: intoOk.from, to: nid }, { from: nid, to: finOk.id })
        } else {
          edges.push({ from: nid, to: finOk.id })
        }
      }
      return { nodes: [...f.nodes, node], edges }
    })
    setSelectedId(nid)
  }

  // Delete a step and heal the chain: every X→node→Y becomes X→Y (deduped).
  // The bridged edge keeps the INCOMING edge's condition (`when`), so deleting a
  // node that a decision/route branch pointed at preserves that branch's
  // condition instead of silently turning it into an unconditional edge.
  function deleteStep(nodeId: string): void {
    markEdited()
    setDraft((f) => {
      if (!f) return f
      const incoming = f.edges.filter((e) => e.to === nodeId)
      const outgoing = f.edges.filter((e) => e.from === nodeId)
      const bridged = incoming.flatMap((i) =>
        outgoing.map((o) => ({ from: i.from, to: o.to, ...(i.when ? { when: i.when } : {}) })),
      )
      const seen = new Set<string>()
      const edges = [
        ...f.edges.filter((e) => e.from !== nodeId && e.to !== nodeId),
        ...bridged,
      ].filter((e) => {
        const k = `${e.from}->${e.to}->${e.when ?? ''}`
        if (seen.has(k)) return false
        seen.add(k)
        return true
      })
      return { nodes: f.nodes.filter((n) => n.id !== nodeId), edges }
    })
    setSelectedId('start')
  }

  // Re-point a linear node's single outgoing edge.
  function setNext(nodeId: string, target: string): void {
    markEdited()
    setDraft((f) =>
      f
        ? {
            ...f,
            edges: [
              ...f.edges.filter((e) => e.from !== nodeId),
              ...(target ? [{ from: nodeId, to: target }] : []),
            ],
          }
        : f,
    )
  }

  // dec branches = the node's outgoing edges ({ to, when }).
  function addBranch(nodeId: string): void {
    markEdited()
    setDraft((f) => {
      if (!f) return f
      const fallback = f.nodes.find((n) => n.type === 'fin')
      return { ...f, edges: [...f.edges, { from: nodeId, to: fallback?.id ?? '', when: 'true' }] }
    })
  }
  function updateBranch(nodeId: string, idx: number, patch: { to?: string; when?: string }): void {
    markEdited()
    setDraft((f) => {
      if (!f) return f
      let n = -1
      const edges = f.edges.map((e) => {
        if (e.from === nodeId) {
          n++
          if (n === idx) return { ...e, ...patch }
        }
        return e
      })
      return { ...f, edges }
    })
  }
  function removeBranch(nodeId: string, idx: number): void {
    markEdited()
    setDraft((f) => {
      if (!f) return f
      let n = -1
      const edges = f.edges.filter((e) => {
        if (e.from === nodeId) {
          n++
          return n !== idx
        }
        return true
      })
      return { ...f, edges }
    })
  }

  // Route (Descubrir) categories. The author edits a single list of "what the
  // customer wants" rows; `writeRouteBranches` keeps the node's config.options
  // (key→label, read by the classifier) and its branch edges (key in `when` →
  // destination) in sync, rebuilding both atomically so the default branch
  // always stays last and a hand-written condition (if any) survives.
  //
  // The rule itself lives in @vysto/shared so the capture-flow generator writes
  // routes exactly the way this form does.
  function writeRoute(nodeId: string, rows: RouteBranchRow[], defaultTo: string): void {
    markEdited()
    setDraft((f) => (f ? writeRouteBranches(f, nodeId, rows, defaultTo) : f))
  }

  // Drag-to-reposition: persist the node's canvas position (saved with the flow;
  // the runtime ignores it, it's editor metadata).
  function moveNode(nodeId: string, position: { x: number; y: number }): void {
    markEdited()
    setDraft((f) =>
      f ? { ...f, nodes: f.nodes.map((n) => (n.id === nodeId ? { ...n, position } : n)) } : f,
    )
  }

  // Drag-to-connect: apply a new edge from the canvas. A decision OR route node
  // branches (adds an edge with a default condition, keeping its other branches);
  // a linear step re-points its single "next"; a terminal (fin) takes no outgoing
  // edge. Live validation then re-checks reachability.
  function connectNodes(from: string, to: string): void {
    // Decided against the current draft rather than inside the setDraft updater:
    // an updater must stay pure, and marking the draft edited from in there is a
    // state update during the render phase (it also ran twice under StrictMode).
    const f = draft
    if (!f) return
    const src = f.nodes.find((n) => n.id === from)
    if (!src || src.type === 'fin') return
    if (src.type === 'dec' || src.type === 'route') {
      // Same branch already drawn: nothing changed, so nothing to mark.
      if (f.edges.some((e) => e.from === from && e.to === to)) return
      markEdited()
      setDraft({ ...f, edges: [...f.edges, { from, to, when: 'true' }] })
      return
    }
    markEdited()
    setDraft({ ...f, edges: [...f.edges.filter((e) => e.from !== from), { from, to }] })
  }

  async function onSave(): Promise<void> {
    if (!draft) return
    setSaving(true)
    setErrors([])
    setConflict(false)
    setSaved(false)
    try {
      await gateway.save(skill!.id, draft, skill!.version)
      setSaved(true)
      await refetch()
    } catch (err) {
      // A version conflict is not a flow error: nothing is wrong with what the
      // author wrote, the base under it moved. Say that, and offer the reload.
      if (errorCode(err) === 'flow_version_conflict') {
        setConflict(true)
      } else {
        setErrors(
          flowSaveErrors(err).map((e) => ({
            ...(e.nodeId ? { nodeId: e.nodeId } : {}),
            rule: e.code,
            message: e.message,
          })),
        )
      }
    } finally {
      setSaving(false)
    }
  }

  /** Discard the local draft and take the published version. Deliberate and
   *  explicit: the author asked for it by clicking, we never do it for them. */
  async function reloadAfterConflict(): Promise<void> {
    setConflict(false)
    setErrors([])
    const res = await refetch()
    const fresh = res.data?.[skillIdx]
    if (fresh) {
      setDraft(structuredClone(fresh.flow))
      setSelectedId(fresh.flow.nodes[0]?.id ?? '')
    }
  }

  const display = toDisplay(draft)
  // Live structural validation (updates as you edit) merged with any blocking
  // errors the save endpoint returned. Dedup by rule+node so a live error and
  // the server's version of the same problem don't both show.
  const liveErrors = validateFlowClient(draft)
  const seenErr = new Set<string>()
  const allErrors = [...liveErrors, ...errors].filter((e) => {
    const k = `${e.rule}:${e.nodeId ?? ''}`
    if (seenErr.has(k)) return false
    seenErr.add(k)
    return true
  })
  const validation: SkillValidation = { valid: allErrors.length === 0, errors: allErrors }
  const outline = phaseOutline(draft)
  const hasPhases = outline.some((g) => g.phase !== '')

  /**
   * "¿A quién se transfiere?" — the one routing picker, rendered identically by
   * the `handoff` step and by a Fin that pasa a una persona. Both write the same
   * `assign_to` shape the runtime reads, so an author does not have to learn two
   * mental models for the same question.
   *
   * Three answers, encoded as select values: `''` = la cola (`{kind:'pool'}`),
   * `'auto'` = the workspace's own rules, and a member id = that person.
   */
  function renderRoutePicker(
    nodeId: string,
    cfg: Record<string, unknown>,
    htmlFor: string,
  ): JSX.Element | null {
    if (!gateway.assignableMembers) return null
    const assignTo = cfg.assign_to as
      | { kind: 'pool' | 'auto' }
      | { kind: 'agent'; agent_id: string }
      | undefined
    const value =
      assignTo?.kind === 'agent' ? assignTo.agent_id : assignTo?.kind === 'auto' ? 'auto' : ''
    return (
      <FormField label="¿A quién se transfiere?" htmlFor={htmlFor}>
        <Select
          value={value}
          onChange={(e) =>
            updateNodeConfig(
              nodeId,
              e.target.value === ''
                ? { assign_to: { kind: 'pool' } }
                : e.target.value === 'auto'
                  ? { assign_to: { kind: 'auto' } }
                  : { assign_to: { kind: 'agent', agent_id: e.target.value } },
            )
          }
          options={[
            { value: '', label: 'A la cola (cualquier asesor disponible)' },
            { value: 'auto', label: 'Automático (según la etapa y las reglas del espacio)' },
            ...(assignableMembers ?? []).map((m) => ({ value: m.id, label: m.name })),
          ]}
        />
      </FormField>
    )
  }

  function renderProperties(node: FlowNodeData): JSX.Element {
    const raw = draft!.nodes.find((n) => n.id === node.id)
    if (!raw) return <p className="text-[12px] text-text-2">Selecciona un paso.</p>
    const cfg = raw.config
    const others = draft!.nodes
      .filter((n) => n.id !== raw.id)
      .map((n) => ({ value: n.id, label: stepLabel(n) }))
    const currentNext = draft!.edges.find((e) => e.from === raw.id)?.to ?? ''
    const branches = draft!.edges.filter((e) => e.from === raw.id)
    // Variables a Decisión can branch on (captured answers + CRM/contact roots).
    const decVars = flowVars(draft!)

    let body: JSX.Element
    if (raw.type === 'msg') {
      // The entry node's text is what this flow opens with. Since W4'-4a the
      // greeting's HOME is the configuration (Configuración → Procesos): the
      // agent publish compiles it into this node, so hand-editing it here (only
      // possible on an unmanaged flow — a managed one is read-only) lasts until
      // the next publish. Say so, or an author "fixes" the greeting in the one
      // place the compiler overwrites.
      const isStart =
        raw.id === (draft!.nodes.find((n) => n.id === 'start')?.id ?? draft!.nodes[0]?.id)
      body = (
        <div className="space-y-3">
          <p className="text-[12px] leading-relaxed text-text-2">
            El agente envía este mensaje y continúa al siguiente paso. Puedes usar datos guardados
            con <code className="text-[11px]">{'${nombre}'}</code> (por ejemplo, “Hola{' '}
            <code className="text-[11px]">{'${contact.profile_name}'}</code>”).
          </p>
          {isStart && (
            <p className="text-[12px] leading-relaxed text-text-2">
              Este es el <strong>saludo inicial</strong> de este flujo: lo primero que lee cada
              persona que escribe. El saludo del agente vive en Configuración → Procesos; al
              publicar el agente, se escribe aquí.
            </p>
          )}
          <FormField label="Mensaje que envía el agente" htmlFor="flow-msg">
            <Input
              value={str(cfg.text)}
              onChange={(e) => updateNodeConfig(raw.id, { text: e.target.value })}
              placeholder="Escribe lo que dirá el agente…"
            />
          </FormField>
        </div>
      )
    } else if (raw.type === 'ask') {
      const extract = cfg.extract as { kind?: string; values?: string[] } | undefined
      const extractKind = str(extract?.kind) || 'string'
      const enumValues = Array.isArray(extract?.values) ? extract.values : []
      const askVar = str(cfg.var)
      // El dato de la ficha que esta Pregunta llena, si es uno. Si no lo es, la
      // Pregunta está en "campo libre" y el nombre se escribe abajo.
      const askField = askFichaFields.find((f) => f.key === askVar)
      body = (
        <div className="space-y-4">
          <p className="text-[12px] leading-relaxed text-text-2">
            El agente hace una pregunta, guarda la respuesta (con el nombre que elijas) y continúa.
            Si el cliente responde otra cosa, vuelve a preguntar.
          </p>
          <FormField label="Pregunta al usuario" htmlFor="ask-prompt">
            <Input
              value={str(cfg.prompt)}
              onChange={(e) => updateNodeConfig(raw.id, { prompt: e.target.value })}
            />
          </FormField>
          <FormField label="Guardar la respuesta como" htmlFor="ask-var">
            <Select
              value={askField ? askField.key : FREE_FIELD}
              onChange={(e) => {
                const picked = askFichaFields.find((f) => f.key === e.target.value)
                if (!picked) {
                  // "Campo libre": el nombre se escribe abajo y el tipo NO se
                  // toca — no hay ficha de dónde deducirlo, y sobrescribirlo
                  // borraría lo que el autor ya eligió a mano.
                  updateNodeConfig(raw.id, { var: '' })
                  return
                }
                // Un dato de la ficha trae su tipo consigo: `var` y `extract` se
                // escriben en UNA sola actualización. Ese es el arreglo de F39 —
                // una "Especialidad" que es lista de siete opciones se capturaba
                // como texto libre y se guardaba verbatim. El autor puede cambiar
                // el tipo después con el Select de abajo; esto es el punto de
                // partida correcto, no un candado.
                updateNodeConfig(raw.id, {
                  var: picked.key,
                  // Y SU FUENTE (D21-4). Sin ella, una Pregunta escrita a mano
                  // sobre un dato que toma su lista de la Agenda se horneaba con
                  // las especialidades de HOY y se quedaba con ellas; la misma
                  // pregunta compilada desde la ficha llevaba su `source` y la
                  // volvía a resolver al contestar. El dato es uno, así que la
                  // pregunta tiene que ser una.
                  extract: askExtractForDataType({
                    data_type: picked.dataType,
                    options: picked.options,
                    ...(fieldOptionsSourceOf(picked.optionsSource) !== undefined
                      ? { options_source: fieldOptionsSourceOf(picked.optionsSource) }
                      : {}),
                  }),
                })
              }}
              options={[
                ...askFichaFields.map((f) => ({ value: f.key, label: f.label })),
                { value: FREE_FIELD, label: 'Otro nombre (campo libre)' },
              ]}
            />
          </FormField>
          {!askField && (
            <FormField
              label="Nombre del dato (campo libre)"
              htmlFor="ask-var-libre"
              helper={FREE_FIELD_NOTE}
            >
              <Input
                value={askVar}
                onChange={(e) => updateNodeConfig(raw.id, { var: e.target.value })}
                placeholder="p. ej. fecha"
              />
            </FormField>
          )}
          <FormField label="Tipo de dato esperado" htmlFor="ask-extract">
            <Select
              value={extractKind}
              onChange={(e) =>
                updateNodeConfig(raw.id, {
                  extract:
                    e.target.value === 'enum'
                      ? { kind: 'enum', values: enumValues.length > 0 ? enumValues : [''] }
                      : { kind: e.target.value },
                })
              }
              options={[
                { value: 'string', label: 'Texto libre' },
                { value: 'enum', label: 'Opción de una lista' },
                { value: 'phone_mx', label: 'Teléfono (MX)' },
                { value: 'email', label: 'Correo' },
                { value: 'boolean', label: 'Sí / No' },
                { value: 'datetime_iso', label: 'Fecha y hora' },
                { value: 'monetary_mxn', label: 'Monto (MXN)' },
              ]}
            />
          </FormField>
          {extractKind === 'enum' ? (
            <EnumValuesEditor
              key={raw.id}
              values={enumValues}
              onChange={(values) => updateNodeConfig(raw.id, { extract: { kind: 'enum', values } })}
            />
          ) : null}
        </div>
      )
    } else if (raw.type === 'act') {
      const toolHint = toolCatalog?.find((t) => t.name === str(cfg.tool))?.description
      const isCrmLead = str(cfg.tool) === 'capturar_lead'
      const args = (cfg.args as Record<string, unknown> | undefined) ?? {}
      body = (
        <div className="space-y-4">
          <p className="text-[12px] leading-relaxed text-text-2">
            El agente ejecuta una tarea (por ejemplo guardar el prospecto en el CRM) y continúa.
            Elige la acción y completa sus datos.
          </p>
          <FormField label="Acción a ejecutar" htmlFor="act-tool">
            <Input
              value={str(cfg.tool)}
              onChange={(e) => updateNodeConfig(raw.id, { tool: e.target.value })}
              placeholder="p. ej. capturar_lead"
              list="flow-tools"
            />
            <datalist id="flow-tools">
              {(toolCatalog ?? []).map((t) => (
                <option key={t.name} value={t.name}>
                  {t.description}
                </option>
              ))}
            </datalist>
            {toolHint ? <p className="mt-1 text-[11px] text-text-3">{toolHint}</p> : null}
          </FormField>
          {isCrmLead ? (
            <CrmLeadArgsEditor
              key={raw.id}
              args={args}
              stages={pipelineStages}
              ficha={pipelineFicha}
              onChange={(next) => updateNodeConfig(raw.id, { args: next })}
            />
          ) : (
            <ActArgsEditor
              key={raw.id}
              value={args}
              onChange={(next) => updateNodeConfig(raw.id, { args: next })}
            />
          )}
          <FormField label="Guardar el resultado como" htmlFor="act-out">
            <Input
              value={str(cfg.out)}
              onChange={(e) => updateNodeConfig(raw.id, { out: e.target.value })}
            />
          </FormField>
        </div>
      )
    } else if (raw.type === 'fin') {
      const kind = str(cfg.kind) || 'ok'
      body = (
        <div className="space-y-4">
          <p className="text-[12px] leading-relaxed text-text-2">
            Termina la conversación. <strong>Resolver</strong> = el tema queda atendido y el agente
            queda listo para el siguiente mensaje (que vuelve a empezar el flujo). Para transferir a
            una persona normalmente se usa un paso <em>Pasar con asesor</em>, no un Fin.
          </p>
          <FormField label="Cómo termina este camino" htmlFor="fin-kind">
            <Select
              value={kind}
              onChange={(e) =>
                updateNodeConfig(
                  raw.id,
                  e.target.value === 'handoff'
                    ? { kind: 'handoff', reason: str(cfg.reason) || 'Se requiere atención humana' }
                    : { kind: 'ok' },
                )
              }
              options={[
                { value: 'ok', label: 'Resolver y regresar al agente' },
                { value: 'handoff', label: 'Pasar a una persona' },
              ]}
            />
          </FormField>
          {kind === 'handoff' ? (
            <>
              <FormField label="Motivo (para el registro)" htmlFor="fin-reason">
                <Input
                  value={str(cfg.reason)}
                  onChange={(e) => updateNodeConfig(raw.id, { reason: e.target.value })}
                />
              </FormField>
              {renderRoutePicker(raw.id, cfg, 'fin-assign')}
            </>
          ) : null}
        </div>
      )
    } else if (raw.type === 'answer') {
      body = (
        <div className="space-y-2">
          <p className="text-[12px] leading-relaxed text-text-2">
            El agente responde con la <strong>base de conocimiento</strong> (los documentos que
            subes en Conocimiento), con el tono configurado, y continúa al siguiente paso.
          </p>
          <p className="text-[12px] leading-relaxed text-text-2">
            Si no encuentra nada relacionado, <strong>no inventa</strong>: ofrece pasar con una
            persona o pide reformular (ese mensaje se edita en <em>Ajustes</em>). Para que responda
            de verdad, sube información en Conocimiento.
          </p>
        </div>
      )
    } else if (raw.type === 'route') {
      body = (
        <div className="space-y-4">
          <p className="text-[12px] leading-relaxed text-text-2">
            El agente lee el mensaje del cliente y decide qué quiere. Tú defines abajo, en tus
            palabras, las categorías de tu negocio (por ejemplo: <em>Quiere comprar un auto</em>,{' '}
            <em>Necesita servicio</em>) y a dónde lleva cada una. No está atado a una industria: si
            no es un hospital, escribe las categorías que correspondan. Si ninguna aplica, se toma
            el camino “en cualquier otro caso”.
          </p>
          <details className="text-[11px] text-text-3">
            <summary className="cursor-pointer">Avanzado: nombre de la variable</summary>
            <FormField label="Guardar la intención como" htmlFor="route-var" className="mt-2">
              <Input
                value={str(cfg.var) || 'intent'}
                onChange={(e) => updateNodeConfig(raw.id, { var: e.target.value })}
                placeholder="intent"
              />
            </FormField>
          </details>
        </div>
      )
    } else if (raw.type === 'call') {
      const subflows = (flows ?? []).filter((f) => f.role !== 'orchestrator' && f.id !== skill?.id)
      const target = str(cfg.skill_id)
      body = (
        <div className="space-y-3">
          <p className="text-[12px] leading-relaxed text-text-2">
            Ejecuta otro flujo (por ejemplo <em>Agendar cita</em> o <em>Calificar lead</em>) y, al
            terminar, regresa a este punto y continúa. Las respuestas guardadas se comparten entre
            los flujos.
          </p>
          {subflows.length === 0 ? (
            <p className="rounded-md bg-surface-2 px-3 py-2 text-[12px] text-text-2">
              Aún no tienes otros flujos para llamar. Crea un flujo (que no sea el principal) y
              vuelve aquí para elegirlo.
            </p>
          ) : (
            <FormField label="Subflujo a ejecutar" htmlFor="call-target">
              <Select
                value={target}
                onChange={(e) => {
                  const picked = subflows.find((f) => f.id === e.target.value)
                  updateNodeConfig(raw.id, {
                    skill_id: e.target.value,
                    ...(picked ? { label: picked.name } : {}),
                  })
                }}
                options={[
                  { value: '', label: 'Elige un subflujo…' },
                  ...subflows.map((f) => ({ value: f.id, label: f.name })),
                ]}
              />
            </FormField>
          )}
        </div>
      )
    } else if (raw.type === 'handoff') {
      body = (
        <div className="space-y-3">
          <p className="text-[12px] leading-relaxed text-text-2">
            Transfiere la conversación a una persona de tu equipo (un asesor). A diferencia de un
            Fin, la conversación puede regresar al agente y continuar el flujo desde el siguiente
            paso.
          </p>
          <FormField label="Motivo (para tu equipo y el registro)" htmlFor="handoff-reason">
            <Input
              value={str(cfg.reason)}
              onChange={(e) => updateNodeConfig(raw.id, { reason: e.target.value })}
              placeholder="p. ej. Pasar con un vendedor"
            />
          </FormField>
          {renderRoutePicker(raw.id, cfg, 'handoff-assign')}
        </div>
      )
    } else {
      body = (
        <p className="text-[12px] leading-relaxed text-text-2">
          Toma un camino según un dato que <strong>ya conoces</strong>: una respuesta guardada
          antes, o el estado del cliente en el CRM (su etapa en el proceso, si es nuevo, o lo que ya
          registramos). Define los caminos abajo con menús, sin escribir código.
        </p>
      )
    }

    const knownPhases = phaseNames(draft!)

    // UN SOLO interruptor para todo el inspector: un <fieldset disabled> nativo
    // apaga cada control que tenga dentro (inputs, selects, textareas, botones),
    // incluidos los que agregue quien venga después. Enhebrar `disabled` campo
    // por campo sería la lista a mano que se queda corta en el siguiente campo.
    return (
      <fieldset disabled={configOwned} className="min-w-0 space-y-4">
        {body}
        <FormField label="Fase (opcional)" htmlFor="node-phase">
          <Input
            value={phaseOf(raw)}
            onChange={(e) => setNodePhase(raw.id, e.target.value)}
            placeholder="p. ej. Identificación"
            list="flow-phase-names"
          />
        </FormField>
        <datalist id="flow-phase-names">
          {knownPhases.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>
        {raw.type !== 'fin' ? (
          <div className="space-y-3 border-t border-line pt-3">
            {raw.type === 'route' ? (
              (() => {
                const { rows, defaultTo } = routeRowsOf(raw, draft!.edges)
                return (
                  <div className="space-y-2">
                    <p className="text-[11px] font-semibold text-text-2">
                      Escribe en tus palabras qué puede querer el cliente. El agente lee su mensaje
                      y elige una sola categoría (gana el primer camino que aplique):
                    </p>
                    {rows.map((r, i) => (
                      <div key={`route-${String(i)}`} className="flex flex-wrap items-center gap-2">
                        <Input
                          value={r.label}
                          onChange={(e) => {
                            const label = e.target.value
                            const key = slugify(label) || `categoria_${String(i + 1)}`
                            const next = rows.map((x, j) =>
                              j === i ? { key, label, to: x.to } : x,
                            )
                            writeRoute(raw.id, next, defaultTo)
                          }}
                          placeholder="p. ej. Quiere comprar un auto"
                          aria-label={`Categoría ${String(i + 1)}`}
                          className="max-w-[230px]"
                        />
                        <span aria-hidden className="text-[12px] text-text-3">
                          →
                        </span>
                        <Select
                          value={r.to}
                          onChange={(e) => {
                            const next = rows.map((x, j) =>
                              j === i ? { ...x, to: e.target.value } : x,
                            )
                            writeRoute(raw.id, next, defaultTo)
                          }}
                          options={others}
                          aria-label={`Destino ${String(i + 1)}`}
                        />
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            writeRoute(
                              raw.id,
                              rows.filter((_, j) => j !== i),
                              defaultTo,
                            )
                          }
                        >
                          Quitar
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        writeRoute(
                          raw.id,
                          [...rows, { key: '', label: '', to: others[0]?.value ?? '' }],
                          defaultTo,
                        )
                      }
                    >
                      Agregar categoría
                    </Button>
                    <div className="flex flex-wrap items-center gap-2 border-t border-line pt-2">
                      <span className="text-[11px] text-text-2">En cualquier otro caso</span>
                      <span aria-hidden className="text-[12px] text-text-3">
                        →
                      </span>
                      <Select
                        value={defaultTo}
                        onChange={(e) => writeRoute(raw.id, rows, e.target.value)}
                        aria-label="Destino por defecto"
                        options={[{ value: '', label: '(sin camino por defecto)' }, ...others]}
                      />
                    </div>
                  </div>
                )
              })()
            ) : raw.type === 'dec' ? (
              <div className="space-y-2">
                <p className="text-[11px] font-semibold text-text-2">
                  Caminos según un dato que ya conoces (gana el primero que se cumpla):
                </p>
                {branches.map((b, i) => {
                  const isDef = isDefaultWhen(b.when)
                  const cond = isDef ? null : parseWhen(str(b.when))
                  const opMeta = cond ? COND_OPS.find((o) => o.value === cond.op) : undefined
                  const setCond = (next: Condition): void =>
                    updateBranch(raw.id, i, { when: buildWhen(next) })
                  return (
                    <div
                      key={`${b.to}-${String(i)}`}
                      className="space-y-2 rounded-md border border-line p-2"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <Select
                          aria-label={`Variable ${String(i + 1)}`}
                          value={isDef ? '__default__' : (cond?.variable ?? '__raw__')}
                          onChange={(e) => {
                            const v = e.target.value
                            if (v === '__default__') updateBranch(raw.id, i, { when: 'true' })
                            else if (v !== '__raw__')
                              setCond({
                                variable: v,
                                op: cond?.op ?? 'eq',
                                value: cond?.value ?? '',
                              })
                          }}
                          options={[
                            { value: '__default__', label: 'En cualquier otro caso' },
                            ...decVars.map((dv) => ({ value: dv.value, label: dv.label })),
                            ...(cond === null && !isDef
                              ? [{ value: '__raw__', label: 'Condición avanzada' }]
                              : []),
                          ]}
                        />
                        {!isDef && cond ? (
                          <>
                            <Select
                              aria-label={`Operador ${String(i + 1)}`}
                              value={cond.op}
                              onChange={(e) =>
                                setCond({ ...cond, op: e.target.value as Condition['op'] })
                              }
                              options={COND_OPS.map((o) => ({ value: o.value, label: o.label }))}
                            />
                            {opMeta?.needsValue ? (
                              <Input
                                aria-label={`Valor ${String(i + 1)}`}
                                value={cond.value}
                                onChange={(e) => setCond({ ...cond, value: e.target.value })}
                                placeholder="valor"
                                className="max-w-[140px]"
                              />
                            ) : null}
                          </>
                        ) : null}
                        {!isDef && cond === null ? (
                          <Input
                            value={str(b.when)}
                            readOnly
                            aria-label={`Condición ${String(i + 1)}`}
                            className="max-w-[220px]"
                          />
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span aria-hidden className="text-[12px] text-text-3">
                          →
                        </span>
                        <Select
                          value={b.to}
                          onChange={(e) => updateBranch(raw.id, i, { to: e.target.value })}
                          options={others}
                          aria-label={`Destino ${String(i + 1)}`}
                        />
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => removeBranch(raw.id, i)}
                        >
                          Quitar
                        </Button>
                      </div>
                    </div>
                  )
                })}
                <Button variant="secondary" size="sm" onClick={() => addBranch(raw.id)}>
                  Agregar camino
                </Button>
              </div>
            ) : (
              <FormField label="Siguiente paso" htmlFor="next-step">
                <Select
                  value={currentNext}
                  onChange={(e) => setNext(raw.id, e.target.value)}
                  placeholder="Elige el siguiente paso…"
                  options={others}
                />
                <p className="mt-1 text-[11px] text-text-3">
                  Puede ser un paso anterior para repetir (ciclo).
                </p>
              </FormField>
            )}
            <Button variant="destructive" size="sm" onClick={() => deleteStep(raw.id)}>
              Eliminar paso
            </Button>
          </div>
        ) : null}
      </fieldset>
    )
  }

  return (
    <div className="mx-auto w-full max-w-5xl pb-24">
      <PageHeader
        breadcrumb={gateway.breadcrumb}
        title="Conversación"
        subtitle={
          skill?.role === 'orchestrator'
            ? 'Flujo principal (orquestador): dirige toda la conversación desde el primer mensaje.'
            : 'Edita lo que dice el agente en cada paso del flujo.'
        }
        actions={
          <div className="flex items-center gap-2">
            {gateway.simulator ? (
              <Button variant="secondary" onClick={() => setSimOpen(true)}>
                Probar
              </Button>
            ) : null}
            <Button
              onClick={() => void onSave()}
              loading={saving}
              disabled={!validation.valid || configOwned}
              {...(configOwned
                ? { title: 'Este flujo lo escribe tu configuración de Procesos.' }
                : {})}
            >
              Guardar flujo
            </Button>
          </div>
        }
      />

      {/* F4-1: el encabezado carga SOLO las dos acciones reales (Probar,
          Guardar flujo). Navegar entre flujos y abrir las demás pantallas vive
          en esta barra, que envuelve en 375px en vez de recortar el botón de
          guardar. */}
      <div data-testid="flow-editor-toolbar" className="mt-4 flex flex-wrap items-center gap-2">
        {backButton}
        {flows.length > 1 ? (
          // El ancho tiene que venir del contenedor: Select pinta un
          // <span className="relative block w-full"> y su `className` llega
          // solo al <select> de adentro.
          <div className="w-[260px]">
            <Select
              aria-label="Flujo"
              value={String(skillIdx)}
              onChange={(e) => setSkillIdx(Number(e.target.value))}
              options={flows.map((s, i) => ({
                value: String(i),
                label: flowSelectLabel(s),
              }))}
            />
          </div>
        ) : null}
        {/* Operators only: owners keep the deliberate one-click
            orchestrator path (one agent = one main flow) — the old
            multi-flow menu was removed for them on purpose. The operator
            gateway never sets supportsOrchestrator, so this is the same
            capability-driven rendering as every other button here. */}
        {!gateway.supportsOrchestrator ? (
          <Button variant="secondary" onClick={() => setNewOpen(true)}>
            Nuevo flujo
          </Button>
        ) : null}
        {gateway.saveAsTemplate && skill ? (
          <Button
            variant="ghost"
            onClick={() => {
              setTplName(skill.name)
              setTplOpen(true)
            }}
          >
            Guardar como plantilla
          </Button>
        ) : null}
        {gateway.settings ? (
          <Button variant="ghost" onClick={() => setProfileOpen(true)}>
            Perfil
          </Button>
        ) : null}
        {gateway.kbHref ? (
          <Button variant="ghost" onClick={() => void navigate(gateway.kbHref!)}>
            Conocimiento
          </Button>
        ) : gateway.kb ? (
          <Button variant="ghost" onClick={() => setKbOpen(true)}>
            Conocimiento
          </Button>
        ) : null}
        {gateway.settingsHref ? (
          <Button variant="ghost" onClick={() => void navigate(gateway.settingsHref!)}>
            Ajustes
          </Button>
        ) : gateway.settings ? (
          <Button variant="ghost" onClick={() => setSettingsOpen(true)}>
            Ajustes
          </Button>
        ) : null}
        {gateway.deleteFlow && skill ? (
          <Button variant="ghost" onClick={() => setConfirmDel(true)}>
            Eliminar flujo
          </Button>
        ) : null}
      </div>

      {orchestratorBanner ? <div className="mt-4">{orchestratorBanner}</div> : null}
      {driftBanner ? <div className="mt-4">{driftBanner}</div> : null}
      {configOwnedBanner ? <div className="mt-4">{configOwnedBanner}</div> : null}

      {conflict ? (
        <div className="mt-4" data-testid="flow-version-conflict">
          <Banner tone="warn">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>
                <b>No guardamos tus cambios.</b> Alguien más publicó una versión más nueva de este
                flujo mientras lo editabas. Vuelve a cargarlo para ver los cambios y aplica los
                tuyos otra vez.
              </span>
              <Button variant="secondary" size="sm" onClick={() => void reloadAfterConflict()}>
                Recargar el flujo
              </Button>
            </div>
          </Banner>
        </div>
      ) : null}

      {saved ? <p className="mt-2 text-[12px] text-text-2">Guardado y publicado.</p> : null}
      {!validation.valid ? (
        <p className="mt-2 text-[12px] text-sens">
          El flujo tiene errores que impiden publicarlo. Corrígelos (se marcan abajo) para poder
          guardar.
        </p>
      ) : null}

      {hasPhases ? (
        <nav aria-label="Fases del flujo" className="mt-4 flex flex-wrap gap-2">
          {outline.map((g) => (
            <button
              key={g.phase || '__none__'}
              type="button"
              onClick={() => g.nodes[0] && setSelectedId(g.nodes[0].id)}
              className="rounded-pill border border-line bg-surface px-3 py-1 text-[11px] font-semibold text-text-2 transition-colors hover:bg-hover hover:text-text"
            >
              {g.phase || 'Sin fase'}
              <span className="ml-1.5 text-text-3">{g.nodes.length}</span>
            </button>
          ))}
        </nav>
      ) : null}

      {/* "¿Cómo funciona?" — teaches the conversation model so a non-builder can
          operate the editor: the progression, what each box does, how to connect
          + loop, and how a handoff actually happens. */}
      <details open className="group mt-4 rounded-lg border border-line bg-surface-2 px-4 py-3">
        <summary className="cursor-pointer list-none text-[13px] font-bold text-text">
          ¿Cómo funciona? <span className="font-normal text-text-3 group-open:hidden">(abrir)</span>
        </summary>
        <div className="mt-3 space-y-3 text-[12px] leading-relaxed text-text-2">
          <p>
            El agente recorre el flujo con <strong>cada mensaje</strong>, desde{' '}
            <strong>Inicio</strong>. Lo normal: saluda, <em>descubre</em> qué quiere el cliente,
            responde o ejecuta una tarea, y termina en <strong>Fin</strong> (resuelto) o lo pasa con
            una persona. Ya conoce al cliente desde el primer mensaje: su etapa en el CRM, si es
            nuevo y lo que ya registramos.
          </p>
          <p>
            <strong>Pasar con una persona:</strong> no conectes cada paso a un traspaso. La{' '}
            <em>red de seguridad</em> (botón “Ajustes”) transfiere automáticamente cuando el agente
            se atora. Solo coloca un paso <strong>Pasar con asesor</strong> donde quieras un
            traspaso a propósito (p. ej. después de calificar un prospecto).
          </p>
          <p>
            <strong>Repetir (ciclos):</strong> el “Siguiente paso” de una caja puede apuntar a un
            paso anterior (p. ej. después de Responder, regresar a Descubrir para seguir
            atendiendo).
          </p>
          <p className="text-text-3">
            Cada caja es un paso. <strong>Haz clic</strong> para editarla (panel derecho),{' '}
            <strong>arrástrala</strong> para moverla, y arrastra desde el punto de abajo para{' '}
            <strong>conectarla</strong>.
          </p>
          <ul className="flex flex-wrap gap-x-5 gap-y-1 border-t border-line pt-2">
            {NODE_HELP.map((n) => (
              <li key={n.type} className="text-[11.5px] text-text-3">
                <span className="font-semibold text-text-2">{n.label}:</span> {n.help}
              </li>
            ))}
          </ul>
        </div>
      </details>

      <div className="mt-4">
        <Card>
          <Card.Body>
            <FlowEditor
              skillName={skill.name}
              nodes={display.nodes}
              edges={display.edges}
              selectedId={selectedId}
              onSelectNode={setSelectedId}
              onAddNode={(type) => addStep(type)}
              onMoveNode={moveNode}
              onConnect={connectNodes}
              validation={validation}
              renderProperties={renderProperties}
              readOnly={configOwned}
              readOnlyNote="Vista de solo lectura: este flujo lo escribe tu configuración de Procesos."
            />
          </Card.Body>
        </Card>
      </div>

      {kbOpen && gateway.kb ? (
        <Modal
          open
          onClose={() => setKbOpen(false)}
          title="Conocimiento del agente"
          footer={
            <Button variant="secondary" onClick={() => setKbOpen(false)}>
              Cerrar
            </Button>
          }
        >
          <div className="space-y-3">
            <p className="text-[12px] leading-relaxed text-text-2">
              Sube documentos o páginas con la información de tu negocio. El paso <em>Responder</em>{' '}
              contesta con esto; si no encuentra nada, ofrece pasar con una persona en vez de
              inventar. Aprueba cada fuente para que el agente la use.
            </p>
            <KbPanel scopeKey={gateway.scopeKey} api={gateway.kb} />
          </div>
        </Modal>
      ) : null}
      <Modal
        open={newOpen}
        onClose={() => {
          if (!newBusy) setNewOpen(false)
        }}
        title="Nuevo flujo"
        subtitle="Empieza en blanco o desde una plantilla de la vertical."
        busy={newBusy}
        footer={
          <>
            <Button variant="secondary" onClick={() => setNewOpen(false)} disabled={newBusy}>
              Cancelar
            </Button>
            <Button loading={newBusy} onClick={() => void createTaskFlow()}>
              Crear flujo
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Nombre del flujo" htmlFor="new-flow-name">
            <Input
              id="new-flow-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Recordatorio de cita"
            />
          </FormField>
          <FormField
            label="Empezar desde"
            htmlFor="new-flow-template"
            helper="Las plantillas traen los pasos típicos de la vertical ya armados."
          >
            <Select
              id="new-flow-template"
              value={newTemplate}
              onChange={(e) => setNewTemplate(e.target.value)}
              options={[
                { value: '', label: 'En blanco' },
                ...(catalog?.templates ?? []).map((t) => ({
                  value: t.id,
                  label: t.description ? `${t.name} — ${t.description}` : t.name,
                })),
              ]}
            />
          </FormField>
          <FormField
            label="¿Para qué sirve este flujo?"
            htmlFor="new-flow-intent"
            helper={
              newTemplate
                ? 'Si lo dejas vacío, se usa el disparador que trae la plantilla.'
                : 'Es lo que el cliente quiere lograr. El agente activa el flujo cuando lo detecta.'
            }
          >
            <Select
              id="new-flow-intent"
              value={newIntent}
              onChange={(e) => setNewIntent(e.target.value)}
              options={[
                { value: '', label: newTemplate ? 'La de la plantilla' : 'Selecciona una opción' },
                ...triggerableIntents(catalog?.vertical ?? 'generic').map((i) => ({
                  value: i.id,
                  label: i.label,
                })),
                { value: CUSTOM_INTENT, label: 'Otra (escribirla)' },
              ]}
            />
          </FormField>
          {newIntent === CUSTOM_INTENT ? (
            <FormField
              label="Nombre de la intención"
              htmlFor="new-flow-intent-custom"
              helper="Sin espacios ni acentos, por ejemplo: cotizar_estudio."
            >
              <Input
                id="new-flow-intent-custom"
                value={newIntentCustom}
                onChange={(e) => setNewIntentCustom(e.target.value)}
                placeholder="cotizar_estudio"
              />
            </FormField>
          ) : null}
          {newError ? <Banner tone="warn">{newError}</Banner> : null}
        </div>
      </Modal>

      <Modal
        open={tplOpen}
        onClose={() => {
          if (!tplBusy) setTplOpen(false)
        }}
        title="Guardar como plantilla"
        subtitle="El flujo actual queda disponible para otros clientes de la misma vertical."
        busy={tplBusy}
        footer={
          <>
            <Button variant="secondary" onClick={() => setTplOpen(false)} disabled={tplBusy}>
              Cancelar
            </Button>
            <Button loading={tplBusy} onClick={() => void saveCurrentAsTemplate()}>
              Guardar plantilla
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Nombre" htmlFor="tpl-name">
            <Input id="tpl-name" value={tplName} onChange={(e) => setTplName(e.target.value)} />
          </FormField>
          <FormField label="Descripción" htmlFor="tpl-desc" helper="Qué resuelve y cuándo usarla.">
            <Input
              id="tpl-desc"
              value={tplDesc}
              onChange={(e) => setTplDesc(e.target.value)}
              placeholder="Agenda una cita validando especialidad y horario"
            />
          </FormField>
          {tplError ? <Banner tone="warn">{tplError}</Banner> : null}
        </div>
      </Modal>

      {simOpen && gateway.simulator ? (
        <Modal
          open
          onClose={() => setSimOpen(false)}
          title="Probar conversación"
          footer={
            <Button variant="secondary" onClick={() => setSimOpen(false)}>
              Cerrar
            </Button>
          }
        >
          <div className="space-y-3">
            <p className="text-[12px] leading-relaxed text-text-2">
              Chatea con tu agente como si fueras un cliente. Usa el flujo y el conocimiento que
              tienes ahora (guarda primero los cambios). Nada se envía por WhatsApp.
            </p>
            <SimulatorPanel
              api={gateway.simulator}
              {...(gateway.boardHref ? { boardHref: gateway.boardHref } : {})}
            />
          </div>
        </Modal>
      ) : null}
      {settingsOpen && gateway.settings ? (
        <AgentSettingsModal settings={gateway.settings} onClose={() => setSettingsOpen(false)} />
      ) : null}
      {profileOpen && gateway.settings ? (
        <BusinessProfileModal
          settings={gateway.settings}
          vertical={catalog?.vertical ?? null}
          onClose={() => setProfileOpen(false)}
        />
      ) : null}

      <Modal
        open={confirmDel}
        onClose={() => setConfirmDel(false)}
        title="Eliminar flujo"
        role="alertdialog"
        tone="sens"
        busy={delBusy}
        {...(skill ? { subtitle: `Se eliminará "${skill.name}" y todas sus versiones.` } : {})}
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setConfirmDel(false)}
              disabled={delBusy}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => void deleteCurrentFlow()}
              loading={delBusy}
            >
              Sí, eliminar
            </Button>
          </>
        }
      >
        <p className="text-[12.5px] text-text-2">
          Esta acción no se puede deshacer. Si es el flujo principal, el agente volverá a responder
          con IA por defecto hasta que crees otro.
        </p>
        {delError ? <p className="mt-2 text-[12px] text-sens">{delError}</p> : null}
      </Modal>
    </div>
  )
}

/** Operator route: /admin/tenants/:id/bots/:botId/flows — staff editing a
 *  client's agent (tenant id in the URL). */
export function AdminFlowEditorScreen(): JSX.Element {
  const { id = '', botId = '' } = useParams()
  return <FlowEditorScreen gateway={operatorFlowGateway(id, botId)} />
}

/** Owner route: /asistente/:botId/flujos — a tenant owner editing their OWN
 *  agent (tenant comes from the session, via /me/bots/:botId/flows). */
export function OwnerFlowEditorScreen(): JSX.Element {
  const { botId = '' } = useParams()
  return <FlowEditorScreen gateway={ownerFlowGateway(botId)} />
}
